import { cn } from '@/shared/helpers/classnames'

import { type FC, createContext, useContext, useMemo } from 'react'

import { BlockItemContext } from './BlockItem'
import type { BlockContentProps } from './types'

export const BlockContentContext = createContext({ isInBlockContent: false, variant: 'soft' })

/**
 * Block.Content - Bloc content
 *
 * Can be used inside a static Block or inside a Block (accordion).
 * In the accordion case, it internally composes Block.Content with Radix UI Content.
 *
 */
export const BlockContent: FC<BlockContentProps> = ({ children, variant }) => {
  const context = useContext(BlockItemContext)
  const activeVariant = variant ?? context?.contentVariant ?? 'accent'

  const contextValue = useMemo(
    () => ({ isInBlockContent: true, variant: activeVariant }),
    [activeVariant],
  )

  return (
    <BlockContentContext value={contextValue}>
      <div
        className={cn('block-content', {
          'block-content--soft': activeVariant === 'soft',
          'block-content--accent': activeVariant === 'accent',
        })}
      >
        {children}
      </div>
    </BlockContentContext>
  )
}

BlockContent.displayName = 'BlockContent'
